import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "professional" ? "/profile/edit" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-[#05070C] px-6 py-16">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <div className="label-eyebrow">Begin your journey</div>
          <h1 className="font-display text-4xl md:text-5xl mt-4">Create your <span className="serif-italic text-[#2DD4BF]">account</span></h1>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { id: "client", label: "I'm hiring", sub: "For teams & founders" },
            { id: "professional", label: "I'm an expert", sub: "Retired professional" },
          ].map((r) => (
            <button key={r.id} type="button" onClick={() => set("role", r.id)} data-testid={`role-${r.id}`}
                    className={`rounded-2xl p-5 text-left border transition-all ${
                      form.role === r.id
                        ? "border-[#2DD4BF] bg-[#2DD4BF]/10"
                        : "border-[#142542] bg-[#0F1B33] hover:border-[#2DD4BF]/50"
                    }`}>
              <div className="label-eyebrow opacity-70">{r.sub}</div>
              <div className="font-display text-xl mt-1 text-[#E8ECF3]">{r.label}</div>
            </button>
          ))}
        </div>

        <div className="section-card">
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-[#94A3B8]">Full name</Label>
              <Input data-testid="reg-name" value={form.name} onChange={(e) => set("name", e.target.value)} required
                     className="mt-2 h-12 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Email</Label>
              <Input type="email" data-testid="reg-email" value={form.email} onChange={(e) => set("email", e.target.value)} required
                     className="mt-2 h-12 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Phone <span className="text-xs">(for WhatsApp welcome)</span></Label>
              <Input type="tel" data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                     placeholder="+91 98765 43210"
                     className="mt-2 h-12 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Password</Label>
              <Input type="password" minLength={6} data-testid="reg-password" value={form.password} onChange={(e) => set("password", e.target.value)} required
                     className="mt-2 h-12 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" />
            </div>
            <button type="submit" disabled={busy} className="pill-solid w-full h-12 font-medium" data-testid="reg-submit">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>
        </div>

        <p className="mt-8 text-sm text-[#94A3B8] text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-[#2DD4BF] hover:underline" data-testid="link-login">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
